<template>
  <div class="rounded-md border border-border bg-sheet px-3 py-3">
    <div class="flex items-start gap-2">
      <p class="text-[13.5px] font-semibold flex-1 min-w-0 leading-tight truncate">{{ route.name }}</p>
      <span
        class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
        :class="statusBadgeClass(route.status)"
      >
        <span class="w-1.5 h-1.5 rounded-full" :class="statusDotClass(route.status)" />
        {{ statusLabel(route.status) }}
      </span>
    </div>

    <div class="mt-2 flex items-center gap-x-2 gap-y-1 flex-wrap text-[11px] font-semibold text-[hsl(var(--foreground)/0.85)]">
      <span class="inline-flex items-center gap-0.5"><Ruler class="w-3 h-3 text-ink2" />{{ formatDistance(route.distance_km) }}</span>
      <span class="text-[hsl(var(--muted-foreground)/0.5)]">·</span>
      <span class="inline-flex items-center gap-0.5"><TrendingUp class="w-3 h-3 text-ink2" />+{{ formatElevation(route.elevation_gain_m) }}</span>
      <span class="text-[hsl(var(--muted-foreground)/0.5)]">·</span>
      <span class="inline-flex items-center gap-0.5"><TrendingDown class="w-3 h-3 text-ink2" />-{{ formatElevation(route.elevation_loss_m) }}</span>
      <template v-if="maxElevation != null">
        <span class="text-[hsl(var(--muted-foreground)/0.5)]">·</span>
        <span class="inline-flex items-center gap-0.5"><Mountain class="w-3 h-3 text-ink2" />{{ Math.round(maxElevation) }}{{ $t('units.m') }}</span>
      </template>
      <template v-if="route.technical_grade">
        <span class="text-[hsl(var(--muted-foreground)/0.5)]">·</span>
        <span class="inline-flex items-center gap-1 shrink-0">
          <span class="text-[10px] font-semibold text-ink2 whitespace-nowrap">{{ gradeLabel(route.technical_grade) }}</span>
          <Route class="w-3 h-3" :style="{ color: gradeAccentColor(route.technical_grade) }" />
          <span class="text-[10px] font-semibold" :style="{ color: gradeAccentColor(route.technical_grade) }">T{{ route.technical_grade }}</span>
        </span>
      </template>
    </div>

    <div class="mt-1.5 flex items-center gap-1.5 text-[11px] text-[hsl(var(--foreground)/0.85)]">
      <Clock class="w-3.5 h-3.5 text-ink2 shrink-0" />
      <template v-if="formattedDuration">
        <span>{{ formattedDuration }}</span>
        <InfoTooltip :text="$t('routeDetails.estimatedTimeDisclaimer')" />
      </template>
      <span v-else class="text-ink2">{{ $t('routeDetails.timeInTransit') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Ruler, TrendingUp, TrendingDown, Mountain, Clock, Route } from "lucide-vue-next";
import type { RouteMapItem } from "../types/routes";
import { gradeAccentColor } from "../utils/routeGrade";
import { formatHikingDuration } from "../utils/formatDuration";
import InfoTooltip from "./InfoTooltip.vue";

const props = defineProps<{ route: RouteMapItem }>();

const { t } = useI18n();

const maxElevation = computed(() => props.route.elevation_profile?.max_elevation_m ?? null);

const formattedDuration = computed(() =>
  formatHikingDuration(props.route.estimated_duration_minutes, t("units.h"), t("units.decimal"))
);

const gradeLabel = (grade: number) => t(`routeDetails.grade.${grade}`);

const statusLabel = (s?: string) => {
  if (s === "active") return t("routeStatus.active");
  if (s === "draft") return t("routeStatus.draft");
  if (s === "closed") return t("routeStatus.closed");
  return t("routeStatus.unknown");
};

const statusBadgeClass = (s?: string) => {
  if (s === "active") return "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]";
  if (s === "draft") return "bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]";
  if (s === "closed") return "bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]";
  return "bg-[hsl(var(--muted))] text-ink2";
};

const statusDotClass = (s?: string) => {
  if (s === "active") return "bg-[hsl(var(--success))]";
  if (s === "draft") return "bg-[hsl(var(--warning))]";
  if (s === "closed") return "bg-[hsl(var(--danger))]";
  return "bg-[hsl(var(--muted-foreground))]";
};

const formatDistance = (v?: number | null) => v != null ? `${v.toFixed(1)}${t("units.km")}` : t("routeStatus.unknown");
const formatElevation = (v?: number | null) => v != null ? `${Math.round(v)}${t("units.m")}` : t("routeStatus.unknown");
</script>
